// DeeplinkManager
import { Logger } from "@core/helpers/logger.helpers";
import { App } from "@capacitor/app";

export class DeepLinkManager {
  constructor({ router, defaultRouteName }) {
    this.router = router;
    this.isInitialized = false;
    this.defaultRouteName = defaultRouteName;
    this.authHandlers = new Set();
  }

  async initialize() {
    Logger.info("we initialize the deeplink manager");
    if (this.isInitialized) return;

    try {
      await this.registerUrlOpenListener();
      this.isInitialized = true;
    } catch (error) {
      Logger.error("Failed to initialize DeeplinkManager:", error);
    }
  }

  addAuthHandler(handler) {
    Logger.info("Adding auth handler:", handler);
    this.authHandlers.add(handler);
  }

  async registerUrlOpenListener() {
    return App.addListener("appUrlOpen", async (data) => {
      Logger.info("URL open event:", data);

      // we execute the authentication flow first if we have auth handlers
      if (this.authHandlers.size > 0) {
        await this.handleAuthenticationFlow(data);
      }

      await this.handleUrlOpen(data);
    });
  }

  async handleAuthenticationFlow(data) {
    // we skip the authentication flow if no auth handlers are registered
    if (this.authHandlers.size === 0) {
      Logger.info("No auth handlers registered, skipping authentication flow");
      return;
    }

    try {
      // we execute all auth handlers
      for (const handler of this.authHandlers) {
        if (handler) {
          await handler(data);
        }
      }
    } catch (error) {
      Logger.error("Error in authentication flow:", error);
    }
  }

  async handleUrlOpen(data) {
    try {
      const { url } = data;
      Logger.info("Handling URL open:", url);

      const urlInfo = this.parseUrl(url);

      if (!urlInfo) {
        await this.navigateToDefault();
        return;
      }

      await this.navigateToRoute(urlInfo);
    } catch (error) {
      Logger.error("Error handling URL open:", error);
      await this.navigateToDefault();
    }
  }

  parseUrl(url) {
    const [baseUrl, hash] = url.split("#");

    if (!hash) return null;

    const [slug, queryPart] = hash.split("?");
    const queryString = queryPart ? `?${queryPart}` : "";
    const fullUrl = new URL(baseUrl + queryString);
    const queryParams = Object.fromEntries(new URLSearchParams(fullUrl.search));

    return {
      slug,
      queryParams,
    };
  }

  async navigateToRoute({ slug, queryParams }) {
    Logger.info("Navigating to:", { slug, queryParams });

    // Add delay for app initialization
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      await this.router.push({
        path: slug,
        query: queryParams,
      });
    } catch (error) {
      Logger.error("Navigation error:", error);
      await this.navigateToDefault();
    }
  }

  async navigateToDefault() {
    try {
      await this.router.replace({
        name: this.defaultRouteName,
      });
    } catch (error) {
      Logger.error("Default navigation error:", error);
    }
  }
}

// DeeplinkService
class DeepLinkService {
  static instance = null;
  static getInstance({ router, defaultRouteName }) {
    Logger.info("DeepLinkService.getInstance");
    if (!DeepLinkService.instance) {
      DeepLinkService.instance = new DeepLinkManager({ router, defaultRouteName });
    }
    return DeepLinkService.instance;
  }
}

export default DeepLinkService;
